import { useSearchParams } from 'react-router-dom'

/**
 * A URL-backed choice from a fixed list: an unknown value reads as the default, so a
 * stray param can never send the API a value it does not know, and choosing the
 * default removes the param, so an unmodified page keeps a plain URL.
 */
export function useUrlChoice<T extends string>(param: string, options: readonly T[], fallback: T): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(param)
  const value = (options as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback
  const set = (next: T) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === fallback) updated.delete(param)
        else updated.set(param, next)
        return updated
      },
      { replace: true },
    )
  return [value, set]
}
